import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/api';
import { supabase } from '../config/supabase';

interface User {
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  user_department?: string;
  user_phone_number?: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  role_name?: string;
  department_name?: string;
}

interface Permission {
  module_name: string;
  module_path: string;
  can_read: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
}

interface AuthContextType {
  user: User | null;
  permissions: Permission[];
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string, schema?: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<User | undefined>;
  hasPermission: (moduleName: string, permission: string) => boolean;
}

interface RegisterData {
  user_name: string;
  user_email: string;
  user_password: string;
  user_role?: string;
  user_department?: string;
  user_phone_number?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Initialize auth state — check Supabase session first, then localStorage fallback
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 1. Check for existing Supabase session
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.access_token) {
          // Supabase session exists — use it
          setToken(session.access_token);
          await loadProfileAndPermissions(session.access_token);
        } else {
          // 2. Fallback: check localStorage (for custom JWT sessions)
          const storedToken = localStorage.getItem('auth_token');
          const storedUser = localStorage.getItem('auth_user');

          if (storedToken && storedUser) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
            const storedPermissions = localStorage.getItem('auth_permissions');
            if (storedPermissions) {
              setPermissions(JSON.parse(storedPermissions));
              setPermissionsLoaded(true);
            }

            // Verify token is still valid
            try {
              const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.PROFILE}`, {
                headers: { 'Authorization': `Bearer ${storedToken}`, 'Content-Type': 'application/json' },
              });
              if (!response.ok && response.status === 401) {
                clearAuthState();
              } else if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                  setUser(data.data);
                  localStorage.setItem('auth_user', JSON.stringify(data.data));
                }
                await loadUserPermissions(storedToken);
              }
            } catch (error) {
              console.warn('Token verification network error, keeping auth state');
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        clearAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for Supabase auth state changes (e.g., token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        clearAuthState();
      } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        setToken(session.access_token);
        localStorage.setItem('auth_token', session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const clearAuthState = () => {
    setUser(null);
    setPermissions([]);
    setPermissionsLoaded(false);
    setToken(null);
    // Clear ALL app data from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_permissions');
    localStorage.removeItem('active_schema');
    localStorage.removeItem('active_org_id');
    localStorage.removeItem('active_org');
    // Clear sessionStorage and caches too
    sessionStorage.clear();
    if ('caches' in window) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
  };

  const loadProfileAndPermissions = async (authToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.PROFILE}`, {
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setUser(data.data);
          localStorage.setItem('auth_user', JSON.stringify(data.data));
          localStorage.setItem('auth_token', authToken);
        }
      }
      await loadUserPermissions(authToken);
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const loadUserPermissions = async (authToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.PERMISSIONS}`, {
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const userPermissions = data.data || [];
        setPermissions(userPermissions);
        localStorage.setItem('auth_permissions', JSON.stringify(userPermissions));
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const login = async (identifier: string, password: string, schema?: string) => {
    try {
      // Login via backend API (which uses Supabase Auth internally)
      const body: Record<string, string> = { identifier, user_password: password };
      if (schema) body.schema = schema;

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      const { user: userData, token: authToken } = data.data;

      // Also sign in with Supabase client for session management (skip for schema-based login)
      if (!schema) {
        try {
          const email = userData.user_email || identifier;
          await supabase.auth.signInWithPassword({ email, password });
        } catch (e) {
          // Non-critical — backend token still works
          console.warn('Supabase client sign-in failed (non-critical):', e);
        }
      }

      setUser(userData);
      setToken(authToken);
      setPermissions([]);

      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('auth_permissions', JSON.stringify([]));

      // Store active schema if provided (super admin schema login)
      if (schema) {
        localStorage.setItem('active_schema', schema);
      } else {
        localStorage.removeItem('active_schema');
      }

      await loadUserPermissions(authToken);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      await login(userData.user_email, userData.user_password);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Sign out from Supabase
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('Supabase signout failed:', e);
    }
    clearAuthState();
  };

  const updateProfile = async (userData: Partial<User>) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.PROFILE}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Profile update failed');
      const updatedUser = data.data;
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Password change failed');

      // Also update in Supabase client session
      try {
        await supabase.auth.updateUser({ password: newPassword });
      } catch (e) {
        // Non-critical
      }
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    try {
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.PROFILE}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch profile');
      if (data.success && data.data) {
        setUser(data.data);
        localStorage.setItem('auth_user', JSON.stringify(data.data));
        return data.data;
      }
    } catch (error) {
      console.error('Profile refresh error:', error);
      throw error;
    }
  };

  const hasPermission = (moduleName: string, permission: string): boolean => {
    // Check both user_role (ID) and role_name (display name)
    const roleId = (user?.user_role || '').toLowerCase().trim();
    const roleName = (user?.role_name || '').toLowerCase().trim();
    const adminRoles = ['admin', 'super_admin', 'super admin', 'system_administrator', 'system administrator', 'administrator'];
    if (adminRoles.includes(roleId) || adminRoles.includes(roleName)) return true;
    if (!permissionsLoaded) return true;
    const modulePermission = permissions.find(
      p => p.module_name?.toLowerCase() === moduleName.toLowerCase()
    );
    if (!modulePermission) return false;
    return (modulePermission as any)[permission] === true;
  };

  const value: AuthContextType = {
    user, permissions, token, isAuthenticated, isLoading,
    login, register, logout, updateProfile, changePassword, refreshProfile, hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
